import { IAddressSearchBounds } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

/**
 * The rectangle's look on the map: blue, so it does not read as one of the red polygons the
 * area editor draws with the same control.
 */
export const BOUNDS_MAP_COLOR = rydeColors.lightBlue;
export const BOUNDS_MAP_FILL_OPACITY = 0.2;
export const BOUNDS_MAP_WEIGHT = 2;

/** Padding (px) for the fit that frames the rectangle when the map opens. */
export const BOUNDS_MAP_FIT_PADDING_PX = 24;

/**
 * The smallest span the box may be dragged down to, in degrees (~100 m).
 *
 * A corner cannot be pulled through the opposite edge: the result would enclose no area,
 * which Google rejects outright.
 */
export const BOUNDS_MIN_SPAN_DEGREES = 0.001;

/**
 * The four drag handles, each naming the two edges it owns.
 *
 * Cropping rather than reshaping: a corner moves one latitude and one longitude, so the
 * area stays a rectangle by construction instead of being snapped back into one.
 */
export const BOUNDS_CORNERS = [
  { key: "northWest", latitude: "north", longitude: "west" },
  { key: "northEast", latitude: "north", longitude: "east" },
  { key: "southEast", latitude: "south", longitude: "east" },
  { key: "southWest", latitude: "south", longitude: "west" },
] as const satisfies readonly {
  key: string;
  latitude: "north" | "south";
  longitude: "east" | "west";
}[];

/**
 * Decimal places an edge keeps — ~10 m at this latitude.
 *
 * Enough for an area that spans a country, and short enough that the value is not clipped in
 * the field beside the map.
 */
export const BOUNDS_EDGE_PRECISION = 4;

/** What typing an edge starts from when neither a saved rectangle nor a default is loaded. */
export const BOUNDS_EMPTY_EDIT_BASE: IAddressSearchBounds = {
  north: 0,
  south: 0,
  east: 0,
  west: 0,
};

/** The four edges, in the order they are shown: latitudes first, then longitudes. */
export const BOUNDS_EDGES = ["north", "south", "west", "east"] as const;

/** Degrees of longitude the whole tile grid spans at zoom 0, which the preview zoom divides down. */
export const BOUNDS_ZOOM_WORLD_DEGREES = 360;

/** Zoom range the preview stays inside: a whole-world box still reads, a tiny one is not street level. */
export const BOUNDS_ZOOM_MIN = 1;
export const BOUNDS_ZOOM_MAX = 12;

/** One drag handle: which latitude edge and which longitude edge it moves. */
export type TBoundsCorner = (typeof BOUNDS_CORNERS)[number];
